/**
 * "Something's wrong here."
 *
 * The only route a child has to say the app is wrong rather than them
 * ([D-044](../../../docs/DECISIONS.md#d-044)). Without it, a child who meets a
 * broken question just gets it wrong, loses the point, and learns that the app
 * is right and they are not. **That is the real harm, and it is worse than the
 * missing bug report.**
 *
 * Every rule here exists to make it safe to tap:
 *
 * - **It costs nothing.** The answer is marked correct whatever they chose, from
 *   the rule in [`packages/core`](../../../packages/core/src/reports.ts) — so it
 *   holds even if the request never lands.
 * - **Fixed reasons, no free-text box.** A child should not have to write in
 *   order to be heard, and it keeps typed content out of the database entirely
 *   ([D-039](../../../docs/DECISIONS.md#d-039)).
 * - **Sneakers thanks them and moves on.** No form, no "are you sure", no
 *   confirmation. The tap is the whole interaction.
 * - **Nothing can go wrong in a way they see.** The request is fire-and-forget:
 *   the lesson moves on before it lands, and a failure is swallowed. A report
 *   that can error is a report a child learns not to file.
 */

import '@capytalk/tokens/blocks/hint.css';

import { REPORT_REASONS, type ReportReason, report } from '@capytalk/core';
import { useEffect, useState } from 'react';
import type { Client } from './api.js';
import { appVersion } from './version.js';

/** The wording a child understands, from [Review](../../../docs/REVIEW.md). */
const SAYS: Readonly<Record<ReportReason, string>> = {
  wrong_audio: 'The sound is wrong',
  wrong_picture: 'The picture does not match',
  two_answers: 'I think my answer was right too',
  dont_understand: 'I do not understand the question',
  other: 'Something else',
};

/** What the lesson does with a report: mark it correct, and carry on. */
export const reportedVerdict = () => report(0);

/**
 * The small **?** in the corner of every exercise.
 *
 * Deliberately quiet. It is always there and it is never the loudest thing on
 * the screen, because a child looking for it will find it and a child who is
 * fine should not be invited to press it.
 */
export function ReportButton({ onOpen }: { readonly onOpen: () => void }) {
  return (
    <button
      className="chip"
      type="button"
      aria-label="Something is wrong with this question"
      style={{ position: 'absolute', right: 14, top: 8, zIndex: 2 }}
      onClick={onOpen}
    >
      <span aria-hidden="true">?</span>
    </button>
  );
}

/**
 * The reasons, over a dimmed screen.
 *
 * One tap picks a reason and closes everything: there is no second step, and no
 * confirmation. `onReported` is called synchronously so the lesson can move on
 * without waiting for the network.
 */
export function ReportSheet({
  onPick,
  onClose,
}: {
  readonly onPick: (reason: ReportReason) => void;
  readonly onClose: () => void;
}) {
  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    globalThis.addEventListener('keydown', key);
    return () => {
      globalThis.removeEventListener('keydown', key);
    };
  }, [onClose]);

  return (
    <>
      <button
        className="dim"
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        style={{ border: 0, padding: 0 }}
        onClick={onClose}
      />
      <div
        className="pop"
        role="dialog"
        aria-modal="true"
        aria-label="What is wrong with this question?"
      >
        <div className="between">
          <h2>What is wrong?</h2>
          <button
            className="x"
            type="button"
            aria-label="Close"
            style={{ background: 'none', border: 0, font: 'inherit' }}
            onClick={onClose}
          >
            ×
          </button>
        </div>
        {REPORT_REASONS.map((reason) => (
          <button
            key={reason}
            className="btn ghost"
            type="button"
            style={{ marginTop: 8, textAlign: 'left' }}
            onClick={() => {
              onPick(reason);
            }}
          >
            {SAYS[reason]}
          </button>
        ))}
      </div>
    </>
  );
}

/**
 * What Sneakers says back. Shown briefly, then the lesson moves on by itself.
 *
 * Long enough to read, short enough that it is not a screen a child has to
 * dismiss. There is nothing to decide here, so there is nothing to tap.
 */
export const THANKS = 'Thanks! I will go and look at that one.';

/**
 * Send it, and never let it matter whether it arrived.
 *
 * Not awaited by any caller. The child has already been thanked and the lesson
 * has already moved on; a failed report is a lost bug report, which is a cost to
 * us and never to them.
 */
export function sendReport(
  client: Client,
  input: {
    readonly exerciseId: string;
    /** Null in a practice session: the server resolves it from the exercise. */
    readonly lessonId: string | null;
    readonly reason: ReportReason;
  },
): void {
  Promise.resolve()
    .then(() =>
      client.learner.report.mutate({
        ...input,
        // What a triage queue needs to reproduce it: which build the child was
        // looking at. The content version is the server's to fill in — it knows
        // which course this learner is on and what it was seeded at.
        appVersion: appVersion(),
      }),
    )
    .catch(() => {
      // Swallowed on purpose. See the note at the top of this file: a report
      // that can fail visibly is a report a child learns not to file.
    });
}

/** The whole interaction: the button, the sheet, and the thank-you. */
export function useReporting(): {
  readonly picking: boolean;
  readonly thanking: boolean;
  readonly open: () => void;
  readonly close: () => void;
  readonly thank: () => void;
  readonly done: () => void;
} {
  const [picking, setPicking] = useState(false);
  const [thanking, setThanking] = useState(false);
  return {
    picking,
    thanking,
    open: () => {
      setPicking(true);
    },
    close: () => {
      setPicking(false);
    },
    thank: () => {
      setPicking(false);
      setThanking(true);
    },
    done: () => {
      setThanking(false);
    },
  };
}
